import { generateContentWithCache } from './geminiService';
import { parseAIJSON } from '../utils/aiUtils';
import { UserProfile, FinancialConfig, Material, ProductTemplate, SimpleService } from '../types';

/**
 * Contrato oficial de Structured Outputs (Gemini REST API).
 * Diretriz 3: matchedServices e matchedProducts declarados ANTES de reply.
 * Diretriz 3 (Ajuste Fino): clientName e serviceName com nullable: true.
 */
export const QUOTE_CHAT_RESPONSE_SCHEMA = {
  type: "OBJECT",
  properties: {
    clientName: {
      type: "STRING",
      nullable: true,
      description: "Nome do cliente citado ou null se não informado"
    },
    serviceName: {
      type: "STRING",
      nullable: true,
      description: "Nome ou escopo principal da proposta (ex: Gerenciamento de Tráfego)"
    },
    matchedServices: {
      type: "ARRAY",
      description: "Lista de serviços identificados, discriminados individualmente",
      items: {
        type: "OBJECT",
        properties: {
          name: { 
            type: "STRING", 
            description: "Nome descritivo do serviço ou ressalva (ex: Campanha Simples (não inclui artes))" 
          },
          quantity: { 
            type: "NUMBER", 
            description: "Quantidade de unidades ou vezes do serviço (ex: 1)" 
          },
          unitValue: { 
            type: "NUMBER", 
            description: "Valor unitário do serviço em Reais sem multiplicar pela quantidade" 
          },
          isEstimated: {
            type: "BOOLEAN",
            description: "true se o valor foi estimado a partir de horas de trabalho e da Hora Base; false se o usuário informou o preço explícito em Reais"
          },
          isUnitBased: {
            type: "BOOLEAN",
            description: "true se o serviço é precificado por item/unidade/peça/sob demanda (ex: R$ 0,75 por item); false se for valor fechado do serviço"
          },
          unitLabel: {
            type: "STRING",
            description: "Rótulo da unidade caso isUnitBased seja true (ex: 'item', 'un', 'peça', 'hora')"
          }
        },
        required: ["name", "quantity", "unitValue"]
      }
    },
    matchedProducts: {
      type: "ARRAY",
      description: "Lista de materiais, insumos ou kits identificados",
      items: {
        type: "OBJECT",
        properties: {
          name: { 
            type: "STRING", 
            description: "Nome do material ou kit" 
          },
          quantity: { 
            type: "NUMBER", 
            description: "Quantidade consumida" 
          },
          unitValue: { 
            type: "NUMBER", 
            description: "Custo unitário em Reais" 
          }
        },
        required: ["name", "quantity", "unitValue"]
      }
    },
    reply: {
      type: "STRING",
      description: "Mensagem conversacional direta e educada para o usuário baseada estritamente nos itens preenchidos acima"
    }
  },
  required: ["matchedServices", "matchedProducts", "reply"]
};

export interface NormalizedQuoteAiItem {
  name: string;
  quantity: number;
  unitValue: number;
  total: number;
  isPassThrough?: boolean;
  isFromRecipe?: boolean;
  materialId?: string;
  showOnPdf?: boolean;
}

export interface NormalizedQuoteAiOutput {
  clientName: string | null;
  serviceName: string | null;
  services: Array<{ 
    name: string; 
    quantity: number; 
    unitValue: number; 
    value: number; 
    isEstimated?: boolean;
    isUnitBased?: boolean;
    unitLabel?: string;
  }>;
  products: NormalizedQuoteAiItem[];
  itemsSum: number;
  reply: string;
  explanation?: string;
  itemsSummaryText: string;
}

export interface ProcessQuoteChatParams {
  text: string;
  imageBase64?: string;
  history: Array<{ role: 'user' | 'assistant'; content: string }>;
  currentCart: {
    clientName?: string;
    products?: any[];
    services?: any[];
  };
  user: UserProfile;
  config: FinancialConfig;
  catalogs: {
    services?: SimpleService[];
    recipes?: ProductTemplate[];
    materials?: Material[];
  };
  signal?: AbortSignal;
}

/**
 * Construtor puro do systemInstruction.
 * Injeta o Nicho do usuário, o estado atual do carrinho (State Snapshot) e os catálogos.
 */
export function buildQuoteSystemInstruction(
  user: UserProfile,
  config: FinancialConfig,
  currentCart: { clientName?: string; products?: any[]; services?: any[] },
  catalogs: { services?: SimpleService[]; recipes?: ProductTemplate[]; materials?: Material[] }
): string {
  const userName = user.ownerName?.split(' ')[0] || 'Usuário';
  const nicheName = user.niche || 'Prestação de Serviços e Comércio';
  const baseHour = config.baseHourValue || 50;

  const currentProducts = Array.isArray(currentCart.products) ? currentCart.products : [];
  const currentServices = Array.isArray(currentCart.services) ? currentCart.services : [];

  const cartSnapshot = {
    clientName: currentCart.clientName || null,
    services: currentServices.map(s => ({
      name: s.name,
      quantity: Number(s.quantity) || 1,
      unitValue: Number(s.unitValue || s.value || 0),
      isUnitBased: Boolean(s.isUnitBased),
      unitLabel: s.unitLabel || undefined
    })),
    products: currentProducts.map(p => ({
      name: p.name,
      quantity: Number(p.quantity) || 1,
      unitValue: Number(p.unitValue || 0)
    }))
  };

  const materialsList = catalogs.materials || [];
  const recipesList = catalogs.recipes || [];
  const servicesList = catalogs.services || [];

  const servicesCat = (servicesList.length > 0)
    ? `\nCATÁLOGO DE SERVIÇOS DO USUÁRIO:\n${servicesList.map(s => `- Serviço: "${s.name}" | Valor Tabelado: R$ ${Number(s.value || 0).toFixed(2)} (${s.type === 'hourly' ? 'Por Hora' : 'Preço Fixo'})`).join('\n')}`
    : '';

  const recipesCat = (recipesList.length > 0)
    ? `\nRECEITAS E KITS PRÉ-CADASTRADOS (Produtos Fabricados / Pré-Precificados):\n${recipesList.map(r => {
        const calcCost = (r.items || []).reduce((acc, item) => {
          const mat = materialsList.find(m => m.id === item.materialId);
          const yAmt = mat?.yieldAmount || 1;
          const uVal = mat ? (mat.pricePaid / yAmt) : 0;
          return acc + (uVal * (item.quantity || 1));
        }, 0);
        const timeStr = r.executionTimeMinutes ? ` | Tempo: ${r.executionTimeMinutes} min` : '';
        return `- Kit/Receita: "${r.name}" | Custo Base de Insumos: R$ ${calcCost.toFixed(2)}${timeStr}`;
      }).join('\n')}`
    : '';

  const materialsCat = (materialsList.length > 0)
    ? `\nESTOQUE E INVENTÁRIO DE MATERIAIS:\n${materialsList.slice(0, 40).map(m => {
        const yAmt = m.yieldAmount || 1;
        const unitCost = (m.pricePaid || 0) / yAmt;
        const stockStr = (m.stockQuantity !== undefined && m.stockQuantity !== null) ? ` | Estoque atual: ${m.stockQuantity} ${m.unit || 'un'}` : '';
        if (m.itemType === 'produto_venda' || (m.salePrice && m.salePrice > 0)) {
          return `- Produto de Venda Direta: "${m.name}" | Preço de Venda: R$ ${Number(m.salePrice || 0).toFixed(2)}${stockStr}`;
        }
        return `- Insumo/Material: "${m.name}" | Embalagem: R$ ${(m.pricePaid || 0).toFixed(2)} (${yAmt} ${m.unit || 'un'}) | Custo Unitário: R$ ${unitCost.toFixed(4)} por ${m.unit || 'un'}${stockStr}`;
      }).join('\n')}`
    : '';

  return `Você é o Assistente Operacional de Precificação R-Max.
Seu usuário chama-se ${userName}.
Nicho de Atuação do Usuário: ${nicheName}.
Valor da Hora Base: R$ ${baseHour.toFixed(2)}/h.

ESTADO ATUAL DO ORÇAMENTO (SNAPSHOT DO CARRINHO EM JSON):
${JSON.stringify(cartSnapshot, null, 2)}

CATÁLOGOS DO USUÁRIO:${servicesCat}${recipesCat}${materialsCat}

DIRETRIZES DE OPERAÇÃO E MÁQUINA DE ESTADOS:
1. O orçamento é uma máquina de estados: você recebe o SNAPSHOT ATUAL acima e o comando do usuário. Retorne o NOVO ESTADO COMPLETO e consolidado em 'matchedServices' e 'matchedProducts'.
2. PREÇO EXPLÍCITO INFORMADO PELO USUÁRIO (REGRA MÁXIMA):
   - Se o usuário citar QUALQUER valor em Reais (ex: "40 reais", "cobro 120", "bolo por 80", "150"):
     - Se for produto fabricado/confeitaria/mercadoria (ex: Bolo, Torta, Doce, Salgado, Peça): crie o item em 'matchedProducts' com esse preço de venda em 'unitValue', quantity: 1.
     - Se for prestação de serviço (ex: Pintura, Instalação, Mentoria, Faxina): crie o item em 'matchedServices' com esse valor em 'unitValue', quantity: 1, isEstimated: false.
     - NUNCA retorne arrays vazios se o usuário informou um preço explícito em Reais!
3. PRECIFICAÇÃO POR UNIDADE / POR ITEM / SOB DEMANDA:
   - Se o usuário disser que o valor é "por item", "a unidade", "cada", "por peça", "por post" ou locução unitária:
     - Marque SEMPRE 'isUnitBased: true' e 'unitLabel: "item"' (ou "un", "peça").
     - Se o usuário NÃO informou a quantidade total: defina 'quantity: 1' e 'unitValue' com esse valor unitário.
     - No 'reply', seja consultivo: confirme o lançamento unitário e pergunte amigavelmente quantos itens o cliente pretende contratar para calcular o valor global (ou se o orçamento será faturado sob demanda).
4. MUTAÇÃO RETROATIVA DE PREÇO UNITÁRIO / AJUSTE CONVERSACIONAL:
   - Se o usuário disser frases de correção como "tem que colocar que esse valor é por item", "é por item", "esse preço é unitário", "falta colocar que é por unidade":
     - Localize os serviços já existentes no snapshot e atualize-os marcando 'isUnitBased: true' e 'unitLabel: "item"'.
     - Se houver mais de um serviço no snapshot e o usuário não especificou qual, aplique a marcação unitária a todos os serviços com valor inferior ou similar citado.
     - Confirme no 'reply' que os serviços agora estão discriminados com valor unitário.
   - Se o usuário em seguida informar a quantidade (ex: "são 100 na Shopee e 50 no Mercado Pago", "serão 50 itens"):
     - Atualize o campo 'quantity' de cada serviço respectivo, mantendo 'unitValue' com o preço unitário e 'isUnitBased: true'.
5. PRODUTO OU SERVIÇO CITADO SEM PREÇO DEFINIDO:
   - Se o usuário pedir um orçamento (ex: "Quero fazer um orçamento de um bolo de aniversário", "Bolo de 2kg para Mariana"):
     - Identifique o 'clientName' (ex: "Mariana") e o 'serviceName' (ex: "Bolo de Aniversário").
     - Se o usuário não citar valor em Reais e não tiver receita cadastrada no catálogo:
       No 'reply', pergunte educadamente qual é o valor de venda desejado ou quais ingredientes/custos deseja considerar.
       Enquanto aguarda a definição de valor do usuário, deixe 'matchedServices': [] e 'matchedProducts': [].
4. MÚLTIPLOS ITENS E VALORES DIFERENTES (MANDATÓRIO):
   - Se o usuário citar mais de um item ou diferentes valores na mesma fala (ex: "cobro 60 para fazer campanha e 200 para ensinar"):
     Gere itens SEPARADOS com seus respectivos valores.
   - É EXPRESSAMENTE PROIBIDO somar ou consolidar itens distintos em uma única linha!
5. ARITMÉTICA ESTREITA:
   - Preencha SEMPRE 'quantity' e 'unitValue' como números unitários.
   - NUNCA tente calcular ou somar totais dentro do JSON. Nosso sistema computará a matemática exata em TypeScript.
6. MUTAÇÃO E EDIÇÃO DO ESTADO (O USUÁRIO MANDA):
   - Se o usuário pedir para mudar um valor (ex: "muda o bolo para 150"), localize o item no estado, altere 'unitValue' para 150 e mantenha os demais itens.
   - Se o usuário disser "tira o item X", remova-o do array.
7. ESTIMATIVA POR HORAS:
   - SE O USUÁRIO CITAR APENAS TEMPO/HORAS SEM CITAR VALOR EM REAIS (ex: "instalação que vai tomar 3 horas", "2 horas de trabalho"):
     Defina SEMPRE 'quantity: 1'.
     Calcule o valor base multiplicando as horas pela Hora Base do usuário (R$ ${baseHour.toFixed(2)}/h).
     Preencha 'unitValue' com essa multiplicação, 'quantity: 1' e marque 'isEstimated: true'.
8. RECONHECIMENTO DE RECEITAS, KITS E MATERIAIS DO INVENTÁRIO:
   - Se o usuário pedir uma RECEITA ou KIT cadastrado: lance em 'matchedProducts' com o nome e o valor unitário cadastrado na lista acima.
   - Se o usuário citar MATERIAIS do inventário: lance em 'matchedProducts' com a quantidade e o Custo Unitário proporcional.
9. CLIENTE:
   - Se o usuário citar o cliente ("para o André", "cliente Maria"), extraia em 'clientName'. Se já estava informado no snapshot, preserve. Se não souber, retorne null e pergunte educadamente ao final do 'reply'.
10. RESPOSTA CONVERSACIONAL ('reply'):
   - Gere o texto de 'reply' baseado estritamente no que foi processado.
   - Seja direto, profissional, técnico e educado (sem chavões de marketing e sem emojis de estrelas).

CONTRATO DAS CHAVES ESPELHADO PARA COMPATIBILIDADE (ESTRITAMENTE OBRIGATÓRIO):
- clientName: string | null
- serviceName: string | null
- matchedServices: Array<{ name: string, quantity: number, unitValue: number, isEstimated?: boolean, isUnitBased?: boolean, unitLabel?: string }>
- matchedProducts: Array<{ name: string, quantity: number, unitValue: number }>
- reply: string

EXEMPLO DE RESPOSTA OBRIGATÓRIA:
{
  "clientName": null,
  "serviceName": "Edição de Vídeo",
  "matchedServices": [
    { "name": "Edição de Vídeo", "quantity": 1, "unitValue": 50, "isEstimated": false, "isUnitBased": false }
  ],
  "matchedProducts": [],
  "reply": "Orçamento anotado com sucesso para a edição do vídeo no valor de R$ 50,00."
}

REGRA CRÍTICA DE NOMENCLATURA:
- NUNCA use chaves em português como "tipo", "valor", "servico", "mensagem", "itens", "total" ou "descricao".
- Use ESTRITAMENTE as chaves: "matchedServices", "matchedProducts", "reply", "clientName", "serviceName".
- Toda prestação de serviço citada pelo operador DEVE ser lançada dentro do array "matchedServices".`;
}

/**
 * Normaliza deterministicamente o retorno da IA em TypeScript.
 * Diretriz 1 (Ajuste Fino): Não aplica Math.ceil(valor / 5) * 5 em preços explícitos ditados pelo usuário.
 * Aplica arredondamento múltiplo de 5 (Artigo 2) APENAS em estimativas calculadas por horas/taxa base (isEstimated: true).
 * Proteção Sênior: Se a IA enviar quantity > 1 com unitValue já multiplicado pelo total das horas, normaliza quantity para 1.
 * Alta Resiliência: Reconhece schemas alternativos (fallback de Groq/Llama com chaves em português como tipo, valor, servico, mensagem, itens, total).
 */
export function normalizeQuoteAiOutput(
  rawInput: any,
  materialsCatalog: Material[] = [],
  recipesCatalog: ProductTemplate[] = [],
  baseHour: number = 50
): NormalizedQuoteAiOutput {
  // 1. Desempacota se for array (ex: payload de transação ou lista direta)
  let rawJson = rawInput;
  if (Array.isArray(rawInput)) {
    if (rawInput.length > 0 && typeof rawInput[0] === 'object') {
      rawJson = { matchedServices: rawInput };
    } else {
      rawJson = {};
    }
  }

  // 2. Desempacota se vier encapsulado em data, updatedData, orcamento ou proposta
  if (rawJson && typeof rawJson === 'object') {
    if (rawJson.updatedData && typeof rawJson.updatedData === 'object') {
      rawJson = { ...rawJson, ...rawJson.updatedData };
    } else if (rawJson.data && typeof rawJson.data === 'object') {
      rawJson = { ...rawJson, ...rawJson.data };
    } else if (rawJson.orcamento && typeof rawJson.orcamento === 'object') {
      rawJson = { ...rawJson, ...rawJson.orcamento };
    } else if (rawJson.proposta && typeof rawJson.proposta === 'object') {
      rawJson = { ...rawJson, ...rawJson.proposta };
    }
  }

  // 3. Extração resiliente de Cliente
  const rawClient = rawJson?.clientName ?? rawJson?.cliente ?? rawJson?.nome_cliente ?? rawJson?.customer;
  const clientName = typeof rawClient === 'string' && rawClient.trim() !== ''
    ? rawClient.trim()
    : null;

  // 4. Coleta de arrays de serviços candidatos (múltiplas chaves possíveis)
  let candidateServices: any[] = [];
  if (Array.isArray(rawJson?.matchedServices)) {
    candidateServices = rawJson.matchedServices;
  } else if (Array.isArray(rawJson?.services)) {
    candidateServices = rawJson.services;
  } else if (Array.isArray(rawJson?.servicos)) {
    candidateServices = rawJson.servicos;
  } else if (Array.isArray(rawJson?.itens)) {
    candidateServices = rawJson.itens.filter((it: any) => it && it.tipo !== 'produto' && it.type !== 'produto');
  } else if (Array.isArray(rawJson?.items)) {
    candidateServices = rawJson.items.filter((it: any) => it && it.tipo !== 'produto' && it.type !== 'produto');
  }

  // 5. Coleta de arrays de produtos/insumos candidatos
  let candidateProducts: any[] = [];
  if (Array.isArray(rawJson?.matchedProducts)) {
    candidateProducts = rawJson.matchedProducts;
  } else if (Array.isArray(rawJson?.products)) {
    candidateProducts = rawJson.products;
  } else if (Array.isArray(rawJson?.produtos)) {
    candidateProducts = rawJson.produtos;
  } else if (Array.isArray(rawJson?.materiais)) {
    candidateProducts = rawJson.materiais;
  } else if (Array.isArray(rawJson?.materials)) {
    candidateProducts = rawJson.materials;
  } else if (Array.isArray(rawJson?.itens)) {
    candidateProducts = rawJson.itens.filter((it: any) => it && (it.tipo === 'produto' || it.type === 'produto'));
  } else if (Array.isArray(rawJson?.items)) {
    candidateProducts = rawJson.items.filter((it: any) => it && (it.tipo === 'produto' || it.type === 'produto'));
  }

  // 6. Suporte a objeto plano de topo (Single-Item Top-Level), como no caso real do cache:
  // { tipo: "orcamento", valor: 50.00, servico: "video", mensagem: "..." }
  // ou { total: 50.00, servico: "Edição de vídeo" }
  if (candidateServices.length === 0 && candidateProducts.length === 0) {
    const topServiceDesc = rawJson?.servico ?? rawJson?.service ?? rawJson?.descricao ?? rawJson?.description ?? rawJson?.serviceName;
    const topValue = Number(rawJson?.valor ?? rawJson?.value ?? rawJson?.total ?? rawJson?.preco ?? rawJson?.price ?? rawJson?.amount ?? 0);
    const topProductDesc = rawJson?.produto ?? rawJson?.product;

    if (topProductDesc && topValue > 0) {
      candidateProducts.push({
        name: String(topProductDesc).trim(),
        quantity: Math.max(0.01, Number(rawJson?.quantidade ?? rawJson?.quantity ?? 1)),
        unitValue: topValue
      });
    } else if (topServiceDesc && topValue > 0) {
      candidateServices.push({
        name: String(topServiceDesc).trim(),
        quantity: Math.max(0.01, Number(rawJson?.quantidade ?? rawJson?.quantity ?? 1)),
        unitValue: topValue,
        isEstimated: false
      });
    } else if (rawJson?.tipo === 'orcamento' && topValue > 0) {
      candidateServices.push({
        name: 'Serviço Solicitado',
        quantity: 1,
        unitValue: topValue,
        isEstimated: false
      });
    } else if (topValue > 0) {
      candidateServices.push({
        name: 'Serviço / Produção',
        quantity: 1,
        unitValue: topValue,
        isEstimated: false
      });
    }
  }

  // 7. Mapeamento e normalização dos Serviços
  const services = candidateServices.map((s, idx) => {
    let qty = Math.max(0.01, Number(s.quantity ?? s.quantidade ?? s.qtd ?? 1) || 1);
    let unitVal = Math.max(0, Number(s.unitValue ?? s.valor ?? s.value ?? s.preco ?? s.preco_unitario ?? s.price ?? s.amount ?? 0) || 0);
    const isEstimated = Boolean(s.isEstimated);
    const sName = String(s.name ?? s.servico ?? s.descricao ?? s.description ?? s.item ?? s.title ?? `Serviço ${idx + 1}`).trim();

    // Proteção anti-dupla multiplicação em estimativas por hora:
    // Se a IA marcou isEstimated, informou qty > 1 e unitVal já é o total das horas (ou superior),
    // normalizamos qty para 1 para evitar que 3h * R$ 65/h vire 3 * 195 = R$ 585.
    if (isEstimated && qty > 1 && baseHour > 0) {
      const expectedTotal = qty * baseHour;
      if (Math.abs(unitVal - expectedTotal) <= 5 || unitVal >= expectedTotal * 0.9) {
        qty = 1;
      }
    }

    // Regra do Artigo 2: Arredondamento obrigatório múltiplo de 5 APENAS para estimativas automatizadas
    if (isEstimated && unitVal > 0) {
      unitVal = Math.ceil(unitVal / 5) * 5;
    }

    const lineTotal = Math.round(qty * unitVal * 100) / 100;

    // Detecção estruturada de precificação unitária / por item
    const isUnitBased = Boolean(s.isUnitBased) || 
      sName.toLowerCase().includes('por item') || 
      sName.toLowerCase().includes('/item') || 
      sName.toLowerCase().includes('por un') || 
      sName.toLowerCase().includes('por unidade') || 
      sName.toLowerCase().includes('por peça');
    const unitLabel = typeof s.unitLabel === 'string' && s.unitLabel.trim() !== '' 
      ? s.unitLabel.trim() 
      : (isUnitBased ? 'item' : undefined);

    return {
      name: sName || `Serviço ${idx + 1}`,
      quantity: qty,
      unitValue: unitVal,
      value: lineTotal,
      isEstimated,
      isUnitBased,
      unitLabel
    };
  });

  // 8. Mapeamento e normalização dos Produtos
  const products: NormalizedQuoteAiItem[] = candidateProducts.map((p, idx) => {
    const qty = Math.max(0.01, Number(p.quantity ?? p.quantidade ?? p.qtd ?? 1) || 1);
    const unitVal = Math.max(0, Number(p.unitValue ?? p.valor ?? p.value ?? p.preco ?? p.cost ?? p.price ?? 0) || 0);
    const lineTotal = Math.round(qty * unitVal * 100) / 100;
    const name = String(p.name ?? p.produto ?? p.material ?? p.item ?? p.descricao ?? `Insumo ${idx + 1}`).trim();
    const matchedMat = materialsCatalog.find(m => m.name.toLowerCase().trim() === name.toLowerCase());
    const matchedRecipe = !matchedMat ? recipesCatalog.find(r => r.name.toLowerCase().trim() === name.toLowerCase()) : null;

    return {
      name: name || `Insumo ${idx + 1}`,
      quantity: qty,
      unitValue: unitVal,
      total: lineTotal,
      materialId: matchedMat?.id,
      isFromRecipe: Boolean(matchedRecipe),
      isPassThrough: false,
      showOnPdf: true
    };
  });

  // 9. Cálculo estrito e determinístico no TypeScript
  const servicesSum = services.reduce((acc, s) => acc + s.value, 0);
  const productsSum = products.reduce((acc, p) => acc + p.total, 0);
  const itemsSum = Math.round((servicesSum + productsSum) * 100) / 100;

  // 10. Montagem do resumo textual limpo para o balão do chat (Padrão M3)
  const summaryLines: string[] = [];
  if (services.length > 0) {
    services.forEach(s => {
      const qtyStr = s.quantity > 1 ? `${s.quantity}x ` : '';
      const estimatedTag = s.isEstimated ? ' *(estimado por hora)*' : '';
      const unitTag = s.isUnitBased && s.quantity <= 1 ? ` *(por ${s.unitLabel || 'item'})*` : '';
      summaryLines.push(`• ⚡ ${s.name}: ${qtyStr}R$ ${s.value.toFixed(2).replace('.', ',')}${estimatedTag}${unitTag}`);
    });
  }
  if (products.length > 0) {
    products.forEach(p => {
      const qtyStr = p.quantity > 1 ? `${p.quantity}x ` : '';
      summaryLines.push(`• 📦 ${p.name}: ${qtyStr}R$ ${p.total.toFixed(2).replace('.', ',')}`);
    });
  }

  const itemsSummaryText = summaryLines.join('\n');

  // 11. Resposta conversacional resiliente (captura mensagem, reply, etc)
  const rawReply = rawJson?.reply ?? rawJson?.mensagem ?? rawJson?.message ?? rawJson?.resposta ?? rawJson?.content;
  const reply = typeof rawReply === 'string' && rawReply.trim() !== ''
    ? rawReply.trim()
    : (itemsSum > 0 ? 'Orçamento atualizado com sucesso!' : 'Olá! O que você gostaria de orçar hoje?');

  // 12. Nome do serviço principal
  const rawServiceName = rawJson?.serviceName ?? rawJson?.servico ?? rawJson?.nome_servico ?? rawJson?.escopo;
  const serviceName = typeof rawServiceName === 'string' && rawServiceName.trim() !== ''
    ? rawServiceName.trim()
    : (services.length > 0 ? services[0].name : null);

  return {
    clientName,
    serviceName,
    services,
    products,
    itemsSum,
    reply,
    explanation: summaryLines.length > 0 ? `Itens na proposta: ${summaryLines.length}` : undefined,
    itemsSummaryText
  };
}

/**
 * Ponto de entrada oficial para processar mensagens do chat de orçamento.
 * Mantém o componente React 100% livre de prompt e de schema.
 */
export async function processQuoteChatMessage(params: ProcessQuoteChatParams): Promise<NormalizedQuoteAiOutput> {
  const { text, imageBase64, history, currentCart, user, config, catalogs, signal } = params;

  const systemInstructionText = buildQuoteSystemInstruction(user, config, currentCart, catalogs);

  const recentHistory = history.slice(-5);
  const historyLines: string[] = [];
  for (const m of recentHistory) {
    const textContent = m.role === 'assistant'
      ? m.content.split('\n\n📋')[0].split('\n\n*Itens')[0].trim()
      : m.content.trim();
    if (textContent) {
      historyLines.push(`${m.role === 'assistant' ? 'Assistente' : 'Operador'}: ${textContent}`);
    }
  }

  // Turno robusto: injeta o prompt do sistema + histórico + mensagem atual em parts.
  // Isso garante 100% de compatibilidade em qualquer proxy ou backend (Node/Express ou Cloudflare Edge).
  const userParts: any[] = [{ text: systemInstructionText }];
  if (historyLines.length > 0) {
    userParts.push({ text: `Histórico recente da conversa:\n${historyLines.join('\n')}` });
  }
  userParts.push({ text: `Comando atual do operador: "${text}"` });

  if (imageBase64) {
    userParts.push({
      inlineData: {
        mimeType: 'image/jpeg',
        data: imageBase64
      }
    });
  }

  const contents: any[] = [{ role: 'user', parts: userParts }];

  const aiResponse = await generateContentWithCache({
    model: 'gemini-3.5-flash-lite',
    contents,
    systemInstruction: {
      parts: [{ text: systemInstructionText }]
    },
    config: {
      responseMimeType: 'application/json',
      responseSchema: QUOTE_CHAT_RESPONSE_SCHEMA,
      temperature: 0.1,
    },
    signal,
    skipCache: true
  });

  const resText = aiResponse.text || '';
  const parsed = parseAIJSON<any>(resText, null);

  if (!parsed) {
    throw new Error('Falha ao estruturar os dados do orçamento. Por favor, tente novamente.');
  }

  return normalizeQuoteAiOutput(parsed, catalogs.materials || [], catalogs.recipes || [], config.baseHourValue || 50);
}
